import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import FilesContainer from '../FilesContainer';
import {
  authenticateSession,
  reverifySession,
} from '@wetransfer/transfer/api/orders';
import {
  POLL_ORDER_STATUS_INTERVAL,
  getCurrentOrderAccessToken,
  getVerifyToken,
  setCurrentOrderAccessToken,
  unsetCurrentOrderAccessToken,
} from '@wetransfer/transfer/helper/paidTransfers';
import { useEffectOnce } from '@wetransfer/transfer/hooks/useEffectOnce';
import { trackAmplitudeEvent } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import {
  OrderStatus,
  resetCurrentOrder,
  selectCurrentOrder,
  resetCurrentOrderError,
  selectCurrentOrderError,
  OrderErrorType,
} from '@wetransfer/transfer/store/currentOrder';
import { loadCurrentOrder } from '@wetransfer/transfer/store/helper/currentOrder';
import {
  selectTransferSecretParam,
  selectFileIdParam,
  selectRecipientIdParam,
} from '@wetransfer/transfer/store/selector/route';
import {
  selectTransfer,
  selectTransferId,
} from '@wetransfer/transfer/store/selector/transfer';
import {
  NormalizedTransfer,
  SellerAccountStatus,
} from '@wetransfer/transfer/transfer-bundle/types/transfers';

import Blocked from './Blocked';
import { Buy } from './Buy';
import {
  TRANSFER_PURCHASE_FUNNEL__PAYMENT_COMPLETE__EVENT,
  TRANSFER_PURCHASE_FUNNEL__PAYMENT_FAILED__EVENT,
  TRANSFER_PURCHASE_FUNNEL__PAYMENT_PROCESSING__EVENT,
} from './constants';
import { DeviceVerify } from './DeviceVerify';
import Processing from './Processing';

/**
 * Either renders the Files component or the purchase flow if the transfer is a paid transfer
 */
export default function DownloadableTransfer(): JSX.Element {
  const dispatch = useDispatch();
  const lastOrderStatusTracked = useRef<OrderStatus | null>(null);
  const pollTimeout = useRef<NodeJS.Timeout | null>(null);
  const currentOrder = useSelector(selectCurrentOrder);
  const currentOrderError = useSelector(selectCurrentOrderError);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isPaymentFailed, setIsPaymentFailed] = useState(false);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [isDeviceUnrecognized, setIsDeviceUnrecognized] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const transfer = useSelector(selectTransfer);
  const normalizedTransfer = transfer as NormalizedTransfer;
  const transferId = normalizedTransfer.id as string;
  const secret = useSelector(selectTransferSecretParam);
  const id = useSelector(selectTransferId) || undefined;
  const fileIdParam = useSelector(selectFileIdParam);
  const recipientId = useSelector(selectRecipientIdParam);

  const fetchCurrentOrder = useCallback(
    (authToken: string) => {
      setIsBusy(true);
      dispatch(loadCurrentOrder(authToken));
    },
    [dispatch]
  );

  const reauthenticateSession = async (verifyToken: string) => {
    setIsBusy(true);

    try {
      const response = await authenticateSession(verifyToken);
      setCurrentOrderAccessToken(transferId, response.body.access_token);
      fetchCurrentOrder(response.body.access_token);
    } catch (error) {
      if (error?.status === 401) {
        setIsDeviceUnrecognized(true);
      } else {
        setHasError(true);
      }

      setIsBusy(false);
    }
  };

  const onGetNewLink = () => {
    reverifySession(getVerifyToken() || getCurrentOrderAccessToken(transferId));
  };

  const resetAuthToken = useCallback(() => {
    // Reset order state in local storage
    // as the order has been abandoned
    unsetCurrentOrderAccessToken(transferId);
  }, [transferId]);

  const resetView = useCallback(() => {
    setHasError(false);
    resetAuthToken();
    // Reset order in store
    dispatch(resetCurrentOrder());
    // Reset current order's error
    dispatch(resetCurrentOrderError());
    // Reset UI to show Buy screen again
    setIsPaymentFailed(false);
    setIsPaymentProcessing(false);
  }, [dispatch, resetAuthToken]);

  const clearPollTimeout = () => {
    if (pollTimeout.current) {
      clearTimeout(pollTimeout.current);
      pollTimeout.current = null;
    }
  };

  // Invoked when the current order is fetched with a "processing" status
  const pollCurrentOrder = useCallback(() => {
    // Wait before the previous request finishes
    if (isBusy) {
      return;
    }

    // State changed from "processed" to something else or is "paid"
    if (!isPaymentProcessing || isPaymentSuccess) {
      clearPollTimeout();

      return;
    }

    // Still processing, continue polling
    pollTimeout.current = setTimeout(
      () => fetchCurrentOrder(getCurrentOrderAccessToken(transferId)),
      POLL_ORDER_STATUS_INTERVAL
    );
  }, [
    isBusy,
    isPaymentSuccess,
    isPaymentProcessing,
    fetchCurrentOrder,
    transferId,
  ]);

  useEffectOnce(() => {
    const currentOrderAccessToken = getCurrentOrderAccessToken(transferId);
    const verifyToken = getVerifyToken();

    if (window.location.search.includes('abandoned=true')) {
      resetAuthToken();
    } else if (verifyToken) {
      reauthenticateSession(verifyToken);
    } else if (currentOrderAccessToken) {
      fetchCurrentOrder(currentOrderAccessToken);
    }

    return () => {
      clearPollTimeout();
    };
  });

  useEffect(() => {
    if (currentOrder?.order_status) {
      let trackingEvent = null;
      setIsBusy(false);

      switch (currentOrder.order_status) {
        case OrderStatus.Processing:
          trackingEvent = TRANSFER_PURCHASE_FUNNEL__PAYMENT_PROCESSING__EVENT;
          setIsPaymentProcessing(true);
          pollCurrentOrder();
          break;
        case OrderStatus.Cancelled:
        case OrderStatus.Expired:
          setIsPaymentFailed(true);
          break;
        case OrderStatus.Failed:
          trackingEvent = TRANSFER_PURCHASE_FUNNEL__PAYMENT_FAILED__EVENT;
          setIsPaymentFailed(true);
          break;
        case OrderStatus.Paid:
          trackingEvent = TRANSFER_PURCHASE_FUNNEL__PAYMENT_COMPLETE__EVENT;
          setIsPaymentSuccess(true);
          break;
      }

      // Track and remember so we don't track again
      // as current order is polled and this effect
      // will be called multiple times
      if (
        lastOrderStatusTracked.current !== currentOrder.order_status &&
        trackingEvent
      ) {
        trackAmplitudeEvent(trackingEvent);
        lastOrderStatusTracked.current = currentOrder.order_status;
      }
    }
  }, [currentOrder?.order_status, pollCurrentOrder]);

  useEffect(() => {
    if (currentOrderError) {
      setIsBusy(false);

      if (currentOrderError === OrderErrorType.UnrecognizedDevice) {
        setIsDeviceUnrecognized(true);
      } else if (currentOrderError === OrderErrorType.ExpiredToken) {
        // Show buy screen
        // and remove expired access token
        resetView();
      } else {
        setHasError(true);
      }
    }
  }, [currentOrderError, resetView]);

  if (hasError) {
    return (
      <Blocked
        title="Something went wrong"
        subtitle="Your order could not be processed. Please try again"
        onClickBackButton={resetView}
      />
    );
  }

  if (isDeviceUnrecognized) {
    return <DeviceVerify onClick={onGetNewLink} />;
  }

  if (isPaymentSuccess) {
    return <FilesContainer isReady />;
  }

  if (isPaymentFailed) {
    return (
      <Blocked
        title="Payment failed"
        subtitle="You payment hasn’t been processed. Please try again"
        onClickBackButton={resetView}
      />
    );
  }

  if (isPaymentProcessing) {
    return <Processing description="We’re processing your payment" />;
  }

  const willFetchOrder =
    !currentOrder && getCurrentOrderAccessToken(transferId);

  if (willFetchOrder || isBusy) {
    return <Processing description="We’re fetching your download" />;
  }

  return (
    <Buy
      id={id}
      secret={secret}
      recipient={recipientId}
      file_id={fileIdParam}
      transfer={normalizedTransfer}
      canBuy={
        normalizedTransfer?.seller?.account?.status ===
        SellerAccountStatus.ACTIVE
      }
    />
  );
}
